import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import IntegrationCard from '../ui/IntegrationCard';
import Analytics from '../../services/analytics';

// Import icons from react-icons
import { 
  SiSlack, 
  SiDiscord, 
  SiGmail, 
  SiWhatsapp, 
  SiAmazon, 
  SiSalesforce,
  SiGithub, 
  SiJira
} from "react-icons/si";

const integrations = [
  {
    name: 'Slack',
    description: 'Deploy AI agents in channels and DMs to answer questions and automate tasks.',
    icon: SiSlack({ size: 30 }),
    color: 'bg-blue-100',
  },
  {
    name: 'Discord',
    description: 'Add AI agents to your Discord server for community management and support.',
    icon: SiDiscord({ size: 30 }),
    color: 'bg-purple-100',
  },
  {
    name: 'Gmail',
    description: 'Automate email responses and organization with intelligent AI assistance.',
    icon: SiGmail({ size: 30 }),
    color: 'bg-red-100',
  },
  {
    name: 'WhatsApp',
    description: 'Create AI agents that communicate via WhatsApp for customer support.',
    icon: SiWhatsapp({ size: 30 }),
    color: 'bg-green-100',
  },
  {
    name: 'AWS',
    description: 'Connect your AWS services for automated infrastructure management.',
    icon: SiAmazon({ size: 30 }),
    color: 'bg-orange-100',
  },
  {
    name: 'Salesforce',
    description: 'Integrate AI agents with your CRM for intelligent customer insights.',
    icon: SiSalesforce({ size: 30 }),
    color: 'bg-blue-100',
  },
  {
    name: 'GitHub',
    description: 'Automate code reviews, issue management, and development workflows.',
    icon: SiGithub({ size: 30 }),
    color: 'bg-gray-100',
  },
  {
    name: 'Jira',
    description: 'AI-assisted project management and ticket handling for your team.',
    icon: SiJira({ size: 30 }),
    color: 'bg-blue-100',
  },
];

interface IntegrationsSectionProps {
  openSignupModal: (itemName: string) => void;
}

const IntegrationsSection: React.FC<IntegrationsSectionProps> = ({ openSignupModal }) => {
  const handleLearnMore = (integrationName: string) => {
    Analytics.trackButtonClick('Learn More - ' + integrationName, 'home-page');
    openSignupModal(integrationName);
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.span 
            className="text-primary-600 font-semibold tracking-wide uppercase text-sm"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Seamless Integrations
          </motion.span>
          <motion.h2 
            className="mt-2 text-3xl sm:text-4xl font-bold text-gray-900"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Connect with Your Favorite Tools
          </motion.h2>
          <motion.p 
            className="mt-4 text-xl text-gray-600"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Our AI agents work with the tools you already use, no migration required.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {integrations.map((integration, index) => (
            <IntegrationCard
              key={index}
              name={integration.name}
              description={integration.description}
              icon={integration.icon}
              color={integration.color}
              delay={index * 0.05}
              onLearnMore={handleLearnMore}
            />
          ))}
        </div>
        
        <motion.div 
          className="mt-12 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          <Link 
            to="/integrations" 
            className="inline-flex items-center text-primary-600 font-medium hover:text-primary-700"
          >
            View all integrations
            <svg 
              className="ml-2 h-5 w-5" 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path 
                fillRule="evenodd" 
                d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" 
                clipRule="evenodd"
              />
            </svg>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default IntegrationsSection;